/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from 'react';
import { ShieldBan } from 'lucide-react';
import { chamarApi } from '@/nucleo/auth/cliente-api';
import { Button } from '@/ui/base/button';

const NS = '/bff/v1/j09-s06-dtp-aplicar-penalidade';
const hoje = new Date().toISOString().slice(0, 10);

export function AplicarPenalidadePage() {
  const [estado, setEstado] = useState<any>();
  const [tipo, setTipo] = useState('BLOQUEIO');
  const [inicio, setInicio] = useState(hoje);
  const [fim, setFim] = useState('');
  const [fundamento, setFundamento] = useState(
    'Aplicação cadastral confirmada pela DTP conforme decisão final assinada e vigente.',
  );
  const [reversao, setReversao] = useState(
    'Recurso provido por nova decisão competente, com restauração do estado cadastral e preservação do histórico.',
  );
  const [ocupado, setOcupado] = useState(false);
  async function chamar(caminho: string, corpo?: unknown) {
    setOcupado(true);
    try {
      const resposta = await chamarApi<any>(`${NS}${caminho}`, {
        metodo: caminho === '/state' ? 'GET' : 'POST',
        corpo,
      });
      setEstado(resposta.state);
    } finally {
      setOcupado(false);
    }
  }
  useEffect(() => {
    void chamar('/state');
  }, []);
  if (!estado) return <p role="status">Carregando aplicação de penalidade…</p>;
  const decisao = estado.decisoes.find(
    (d: any) => !estado.aplicacoes.some((p: any) => p.decisao_id === d.id),
  );
  return (
    <main className="w-full min-w-0 space-y-5 overflow-x-hidden">
      <h1 className="text-3xl font-semibold">
        <ShieldBan className="mr-2 inline" />
        {estado.titulo}
      </h1>
      <p>{estado.descricao}</p>
      {estado.alertas.map((a: any) => (
        <p role="alert" key={a.codigo}>
          {a.mensagem} <code>{a.codigo}</code>
        </p>
      ))}
      {decisao && (
        <section className="grid gap-3 rounded-md border p-4 sm:grid-cols-2">
          <p className="sm:col-span-2">
            Decisão assinada <code className="break-all">{decisao.id}</code> · processo{' '}
            {decisao.protocolo}
          </p>
          <label>
            Penalidade
            <select
              aria-label="Penalidade"
              className="block w-full rounded-md border bg-background p-2"
              value={tipo}
              onChange={(e) => setTipo(e.target.value)}
            >
              <option>ADVERTENCIA</option>
              <option>SUSPENSAO</option>
              <option>BLOQUEIO</option>
              <option>DESCREDENCIAMENTO</option>
            </select>
          </label>
          <label>
            Início
            <input
              aria-label="Início"
              className="block w-full rounded-md border bg-background p-2"
              type="date"
              value={inicio}
              onChange={(e) => setInicio(e.target.value)}
            />
          </label>
          <label>
            Fim opcional
            <input
              aria-label="Fim opcional"
              className="block w-full rounded-md border bg-background p-2"
              type="date"
              value={fim}
              onChange={(e) => setFim(e.target.value)}
            />
          </label>
          <label className="sm:col-span-2">
            Fundamento
            <textarea
              aria-label="Fundamento"
              className="block min-h-24 w-full rounded-md border bg-background p-2"
              value={fundamento}
              onChange={(e) => setFundamento(e.target.value)}
            />
          </label>
          <Button
            disabled={ocupado || fundamento.length < 30}
            onClick={() =>
              void chamar('/verbs/aplicar_penalidade', {
                decisaoId: decisao.id,
                tipo,
                inicio,
                fim: fim || undefined,
                fundamento,
                chaveIdempotencia: crypto.randomUUID(),
              })
            }
          >
            Aplicar e comunicar
          </Button>
        </section>
      )}
      <section className="space-y-3">
        <h2 className="text-xl font-semibold">Aplicações e vigências</h2>
        {estado.aplicacoes.map((p: any) => (
          <article className="space-y-2 rounded-md border p-4" key={p.id}>
            <h3 className="font-semibold">
              {p.tipo} ·{' '}
              {p.bloqueia_operacao ? 'OPERAÇÃO BLOQUEADA' : p.reversao ? 'REVERSA' : 'SEM BLOQUEIO'}
            </h3>
            <p>
              Cadastro: {p.status_credenciado} · Processo:{' '}
              {p.processo_encerrado ? 'ENCERRADO' : 'EM CUMPRIMENTO'}
            </p>
            <p>
              Vigência preservada: {p.inicio} até {p.fim ?? 'sem termo final'}
            </p>
            <p>
              {p.notificacoes.length} comunicações entregues · {p.eventos.length} eventos imutáveis
            </p>
            <code className="block break-all">Aplicação {p.id}</code>
            <div className="flex flex-wrap gap-2">
              {!p.processo_encerrado && (
                <Button
                  disabled={ocupado}
                  onClick={() =>
                    void chamar('/verbs/encerrar_processo', {
                      aplicacaoId: p.id,
                      chaveIdempotencia: crypto.randomUUID(),
                    })
                  }
                >
                  Encerrar processo
                </Button>
              )}
              <Button
                variant="outline"
                disabled={ocupado}
                onClick={() =>
                  void chamar('/verbs/verificar_autorizacao_operacional', {
                    aplicacaoId: p.id,
                    chaveIdempotencia: crypto.randomUUID(),
                  })
                }
              >
                Verificar operação
              </Button>
            </div>
            {!p.reversao && (
              <div className="space-y-2">
                <label>
                  Fundamento da nova decisão de recurso
                  <textarea
                    aria-label="Fundamento da reversão"
                    className="block min-h-24 w-full rounded-md border bg-background p-2"
                    value={reversao}
                    onChange={(e) => setReversao(e.target.value)}
                  />
                </label>
                <Button
                  variant="outline"
                  disabled={ocupado || reversao.length < 30}
                  onClick={() =>
                    void chamar('/verbs/registrar_reversao_recurso', {
                      aplicacaoId: p.id,
                      fundamento: reversao,
                      chaveIdempotencia: crypto.randomUUID(),
                    })
                  }
                >
                  Registrar reversão por nova decisão
                </Button>
              </div>
            )}
            {p.reversao && (
              <p>
                Nova decisão: <code className="break-all">{p.reversao.id}</code>
              </p>
            )}
          </article>
        ))}
      </section>
      {estado.resultado && (
        <p role="status">Operação persistida: {JSON.stringify(estado.resultado)}</p>
      )}
    </main>
  );
}
